import { useEffect, useRef, useState } from 'react';
import { encode } from 'uqr';

/** A QR code drawn as SVG squares. The text never leaves this device. */
export function QrCode({ text, label }: { text: string; label: string }) {
  const { data, size } = encode(text, { ecc: 'M', border: 2 });
  const cells: string[] = [];
  data.forEach((row, y) =>
    row.forEach((dark, x) => {
      if (dark) cells.push(`M${x} ${y}h1v1h-1z`);
    }),
  );
  return (
    <svg className="qr" viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect width={size} height={size} fill="#fff" />
      <path d={cells.join('')} fill="#102733" />
    </svg>
  );
}

interface Detected {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<Detected[]>;
}
type DetectorClass = new (options: { formats: string[] }) => BarcodeDetectorLike;

function detectorClass(): DetectorClass | null {
  const found = (globalThis as { BarcodeDetector?: DetectorClass }).BarcodeDetector;
  return found && typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function' ? found : null;
}

/**
 * Scans a QR code with the camera, where the browser can read one itself
 * (Chrome on Android does). Elsewhere it shows nothing and the code is typed
 * instead. The phone apps in phase 11 scan natively. Frames stay on the device.
 */
export function ScanButton({ onScan }: { onScan(text: string): void }) {
  const [open, setOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  // The latest callback, so a new one from the parent does not restart the camera.
  const latest = useRef(onScan);
  useEffect(() => {
    latest.current = onScan;
  });
  const Detector = detectorClass();

  useEffect(() => {
    if (!open || !Detector) return;
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    let stopped = false;
    const stop = () => {
      stopped = true;
      if (timer) clearInterval(timer);
      for (const track of stream?.getTracks() ?? []) track.stop();
    };
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        if (stopped || !video.current) return stop();
        video.current.srcObject = stream;
        await video.current.play();
        const detector = new Detector({ formats: ['qr_code'] });
        timer = setInterval(() => {
          if (!video.current) return;
          void detector.detect(video.current).then((codes) => {
            const text = codes[0]?.rawValue;
            if (text && !stopped) {
              stop();
              setOpen(false);
              latest.current(text);
            }
          }, () => undefined);
        }, 300);
      } catch {
        setProblem('The camera could not be opened. Type the code instead.');
        setOpen(false);
      }
    })();
    return stop;
  }, [open, Detector]);

  if (!Detector) return null;
  return (
    <div className="scan">
      {open ? (
        <>
          <video ref={video} className="scan-video" muted playsInline aria-label="Camera view for scanning" />
          <button type="button" className="button secondary small" onClick={() => setOpen(false)}>
            Stop scanning
          </button>
        </>
      ) : (
        <button type="button" className="button secondary small" onClick={() => setOpen(true)}>
          Scan the patient’s QR code
        </button>
      )}
      {problem ? <p className="muted">{problem}</p> : null}
    </div>
  );
}
